import React, { useState, useEffect, useRef } from 'react';
import { Check, X, Trash2, ArrowLeft, Edit2, Pin, PinOff } from 'lucide-react';
import { collection, query, orderBy, getDocs, doc, deleteDoc, addDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import { useNavigate } from 'react-router-dom';
import AddWordModal from './AddWordModal';
import WordCard from './WordCard';
import { translations } from './lang';

const Manage = ({ user, lang }) => {
    const navigate = useNavigate();
    const t = translations[lang] || translations['en'];

    const [requests, setRequests] = useState([]);
    const [words, setWords] = useState([]);
    const [activeTab, setActiveTab] = useState('requests'); // 'requests' or 'words'
    const [loading, setLoading] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingWord, setEditingWord] = useState(null);
    const [previewApproval, setPreviewApproval] = useState(null);
    const [isApproving, setIsApproving] = useState(false);
    const [rejectingRequest, setRejectingRequest] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    const [sendRejectEmail, setSendRejectEmail] = useState(true);
    const [sendApproveEmail, setSendApproveEmail] = useState(true);
    const previewCardRef = useRef(null);

    // ... fetchRequests, fetchWords ...
    const fetchRequests = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, "word_requests"), orderBy("createdAt", "desc"));
            const querySnapshot = await getDocs(q);
            const reqs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setRequests(reqs);
        } catch (err) {
            console.error("Error fetching requests:", err);
            alert("Failed to fetch requests");
        } finally {
            setLoading(false);
        }
    };

    const fetchWords = async () => {
        setLoading(true);
        try {
            // Limited fetch for performance, or fetch all if manageable
            const q = query(collection(db, "words"), orderBy("word", "asc"));
            const querySnapshot = await getDocs(q);
            const w = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setWords(w);
        } catch (err) {
            console.error("Error fetching words:", err);
            alert("Failed to fetch words");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'requests') {
            fetchRequests();
        } else {
            fetchWords();
        }
    }, [activeTab]);

    // ... handlers ...

    const handleApprove = (request) => {
        setPreviewApproval(request);
        setSendApproveEmail(!!request.authorEmail);
    };

    const confirmApprove = async () => {
        if (!previewApproval) return;
        
        setIsApproving(true);
        try {
            let base64Image = null;
            if (previewCardRef.current && previewCardRef.current.generateCardBlob) {
                const blob = await previewCardRef.current.generateCardBlob();
                if (blob) {
                    base64Image = await new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.readAsDataURL(blob);
                    });
                }
            }

            if (sendApproveEmail && previewApproval.authorEmail && base64Image) {
                try {
                    const response = await fetch('/api/send-email', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            to: previewApproval.authorEmail,
                            word: previewApproval.word,
                            image: base64Image
                        })
                    });
                    const result = await response.json();
                    if (!response.ok) {
                        console.warn("Email API error:", result);
                    }
                } catch (emailErr) {
                    console.error("Error sending email fetch request:", emailErr);
                }
            }

            const newWordData = { ...previewApproval };
            delete newWordData.id;
            delete newWordData.isRequest;

            await addDoc(collection(db, "words"), {
                ...newWordData,
                approvedAt: serverTimestamp(),
                approvedBy: user.email
            });
            await deleteDoc(doc(db, "word_requests", previewApproval.id));
            setRequests(requests.filter(r => r.id !== previewApproval.id));
            
            alert(t?.wordApprovedSuccess || "Word approved and added to dictionary!");
            setPreviewApproval(null);
        } catch (err) {
            console.error("Error approving word:", err);
            alert("Error approving word.");
        } finally {
            setIsApproving(false);
        }
    };

    const handleRejectClick = (request) => {
        setRejectingRequest(request);
        setRejectReason("");
        setSendRejectEmail(!!request.authorEmail);
    };

    const confirmReject = async () => {
        if (!rejectingRequest) return;
        setIsApproving(true);

        try {
            if (sendRejectEmail && rejectingRequest.authorEmail) {
                try {
                    const response = await fetch('/api/send-email', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            to: rejectingRequest.authorEmail,
                            word: rejectingRequest.word,
                            action: 'reject',
                            reason: rejectReason
                        })
                    });
                    if (!response.ok) {
                        const result = await response.json();
                        console.warn("Email API error:", result);
                    }
                } catch (emailErr) {
                    console.error("Error sending email fetch request:", emailErr);
                }
            }

            await deleteDoc(doc(db, "word_requests", rejectingRequest.id));
            setRequests(requests.filter(r => r.id !== rejectingRequest.id));
            
            setRejectingRequest(null);
        } catch (err) {
            console.error("Error rejecting request:", err);
            alert("Error rejecting request.");
        } finally {
            setIsApproving(false);
        }
    };

    const handleDeleteWord = async (id) => {
        if (!window.confirm("Are you sure you want to delete this word from the dictionary?")) return;
        try {
            await deleteDoc(doc(db, "words", id));
            setWords(words.filter(w => w.id !== id));
        } catch (err) {
            console.error("Error deleting word:", err);
        }
    };

    const togglePin = async (word) => {
        try {
            const wordRef = doc(db, "words", word.id);
            await updateDoc(wordRef, { pinned: !word.pinned });
            // Update local state to reflect change immediately
            setWords(words.map(w => w.id === word.id ? { ...w, pinned: !w.pinned } : w));
        } catch (err) {
            console.error("Error toggling pin:", err);
            alert("Failed to update pin status");
        }
    };

    const handleEditWord = (word) => {
        setEditingWord(word);
        setShowEditModal(true);
    };

    const handleEditRequest = (req) => {
        setEditingWord({ ...req, isRequest: true });
        setShowEditModal(true);
    };

    const handleCloseModal = () => {
        setShowEditModal(false);
        setEditingWord(null);
        if (activeTab === 'words') {
            fetchWords(); // Refresh list to show updates
        } else if (activeTab === 'requests') {
            fetchRequests(); // Refresh requests list
        }
    };

    return (
        <div className="min-h-screen bg-[#f0f0f0] font-sans p-4 md:p-8 flex flex-col items-center">
            {/* Header */}
            <header className="w-full max-w-6xl flex justify-between items-center mb-8">
                <button
                    onClick={() => navigate('/')}
                    className="flex items-center gap-2 font-black uppercase hover:underline"
                >
                    <ArrowLeft size={20} /> {t?.backToDict || 'Back to Dictionary'}
                </button>
                <h1 className="text-3xl font-black uppercase">{t?.dashboardTitle || 'Admin Dashboard'}</h1>
            </header>

            {/* Tabs */}
            <div className="w-full max-w-6xl mb-6 flex gap-4">
                <button
                    onClick={() => setActiveTab('requests')}
                    className={`px-6 py-3 font-black uppercase border-2 border-black transition-all ${activeTab === 'requests'
                        ? 'bg-[#FF66C4] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
                        : 'bg-white hover:bg-gray-100'
                        }`}
                >
                    {t?.tabRequests || 'Pending Requests'} ({requests.length})
                </button>
                <button
                    onClick={() => setActiveTab('words')}
                    className={`px-6 py-3 font-black uppercase border-2 border-black transition-all ${activeTab === 'words'
                        ? 'bg-[#5CE1E6] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
                        : 'bg-white hover:bg-gray-100'
                        }`}
                >
                    {t?.tabWords || 'All Words'}
                </button>
            </div>

            {/* Content */}
            <div className="w-full max-w-6xl bg-white border-4 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] min-h-[500px]">
                {loading ? (
                    <div className="text-center font-bold text-xl p-10">Loading...</div>
                ) : (
                    <div className="flex flex-col gap-4">
                        {activeTab === 'requests' && (
                            requests.length === 0 ? (
                                <p className="text-center text-gray-500 font-bold p-10">{t?.noRequests || 'No pending requests.'}</p>
                            ) : (
                                requests.map(req => (
                                    <div key={req.id} className="border-2 border-black p-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-gray-50">
                                        <div>
                                            <h3 className="text-xl font-black">{req.word}</h3>
                                            <p className="text-sm text-gray-600">
                                                {req.meanings?.[0]?.definitions?.[0]?.definition || (t?.noDef || "No definition provided")}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-1">{t?.requestedBy || 'Requested by:'} {req.authorEmail}</p>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleApprove(req)}
                                                className="px-4 py-2 bg-[#7ED957] border-2 border-black font-bold flex items-center gap-2 hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-y-1 transition-all"
                                                title={t?.approveBtn || 'Approve'}
                                            >
                                                <Check size={18} /> <span className="hidden md:inline">{t?.approveBtn || 'Approve'}</span>
                                            </button>
                                            <button
                                                onClick={() => handleEditRequest(req)}
                                                className="px-4 py-2 bg-[#5CE1E6] border-2 border-black font-bold flex items-center gap-2 hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-y-1 transition-all"
                                                title={t?.editBtn || 'Edit'}
                                            >
                                                <Edit2 size={18} /> <span className="hidden md:inline">{t?.editBtn || 'Edit'}</span>
                                            </button>
                                            <button
                                                onClick={() => handleRejectClick(req)}
                                                className="px-4 py-2 bg-[#FF66C4] border-2 border-black font-bold flex items-center gap-2 hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-y-1 transition-all"
                                                title={t?.rejectBtn || 'Reject'}
                                            >
                                                <X size={18} /> <span className="hidden md:inline">{t?.rejectBtn || 'Reject'}</span>
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )
                        )}

                        {activeTab === 'words' && (
                            words.length === 0 ? (
                                <p className="text-center text-gray-500 font-bold p-10">{t?.noWordsFound || 'No words found.'}</p>
                            ) : (
                                words.map(wordItem => (
                                    <div key={wordItem.id} className="border-b-2 border-gray-200 last:border-0 py-3 flex justify-between items-center hover:bg-gray-50 px-2">
                                        <div className="flex items-center gap-2">
                                            {wordItem.pinned && <Pin size={16} className="text-[#FF66C4] fill-current" />}
                                            <span className="font-bold text-lg">{wordItem.word}</span>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => togglePin(wordItem)}
                                                className={`p-2 border-2 border-black transition-colors ${wordItem.pinned ? 'bg-[#FF66C4] hover:bg-[#ff85d1]' : 'bg-gray-200 hover:bg-gray-300'}`}
                                                title={wordItem.pinned ? "Unpin Word" : "Pin Word"}
                                            >
                                                {wordItem.pinned ? <PinOff size={20} /> : <Pin size={20} />}
                                            </button>
                                            <button
                                                onClick={() => handleEditWord(wordItem)}
                                                className="p-2 bg-[#5CE1E6] border-2 border-black hover:bg-[#4bc7cc] transition-colors"
                                                title="Edit Word"
                                            >
                                                <Edit2 size={20} />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteWord(wordItem.id)}
                                                className="text-red-500 hover:text-red-700 p-2"
                                                title="Delete Word"
                                            >
                                                <Trash2 size={20} />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )
                        )}
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            {showEditModal && (
                <AddWordModal
                    onClose={handleCloseModal}
                    initialData={editingWord}
                    isAdmin={true}
                    lang={lang}
                    t={t}
                />
            )}

            {/* Rejection Modal */}
            {rejectingRequest && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
                    <div className="bg-white border-4 border-black p-6 max-w-lg w-full shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] flex flex-col items-center relative my-8">
                        <h3 className="text-2xl font-black uppercase mb-4 text-[#FF66C4] w-full text-center">Kelimeyi Reddet: {rejectingRequest.word}</h3>
                        
                        {rejectingRequest.authorEmail ? (
                            <div className="mb-4 w-full">
                                <label className="flex items-center gap-3 font-bold cursor-pointer bg-gray-100 p-3 border-2 border-black w-full text-sm">
                                    <input 
                                        type="checkbox"
                                        checked={sendRejectEmail}
                                        onChange={(e) => setSendRejectEmail(e.target.checked)}
                                        className="w-5 h-5 border-2 border-black accent-[#FF66C4] flex-shrink-0"
                                    />
                                    <span>Kullanıcıya ret e-postası gönderilsin mi? <br/><span className="text-gray-500 font-normal">({rejectingRequest.authorEmail})</span></span>
                                </label>
                            </div>
                        ) : (
                            <p className="mb-4 text-gray-500 italic text-center text-sm w-full">Kullanıcının e-posta adresi olmadığı için bildirim gönderilemez.</p>
                        )}

                        {(sendRejectEmail && rejectingRequest.authorEmail) && (
                            <div className="mb-6 w-full">
                                <label className="block font-black mb-2 uppercase text-sm">Ret Gerekçesi (İsteğe bağlı, mailde görünecek)</label>
                                <textarea 
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    placeholder="Kelimenizin uygun bulunmama sebebi..."
                                    className="w-full border-4 border-black p-3 min-h-[100px] font-medium"
                                />
                            </div>
                        )}

                        <div className="flex gap-4 w-full mt-4">
                            <button 
                                onClick={() => setRejectingRequest(null)}
                                disabled={isApproving}
                                className="flex-1 py-3 bg-gray-200 border-4 border-black font-black uppercase shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:shadow-none transition-all disabled:opacity-50"
                            >
                                İptal
                            </button>
                            <button 
                                onClick={confirmReject}
                                disabled={isApproving}
                                className="flex-1 py-3 bg-[#FF66C4] border-4 border-black font-black uppercase shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:shadow-none transition-all flex items-center justify-center gap-2 disabled:opacity-50 text-white"
                            >
                                {isApproving ? 'Siliniyor...' : 'Onayla'} <Trash2 size={24} strokeWidth={3} />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Approval Preview Modal */}
            {previewApproval && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
                    <div className="bg-white border-4 border-black p-6 md:p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] w-full max-w-xl flex flex-col items-center relative my-8">
                        <button
                            onClick={() => !isApproving && setPreviewApproval(null)}
                            disabled={isApproving}
                            className="absolute top-4 right-4 p-2 bg-red-500 border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-y-1 hover:shadow-none transition-all disabled:opacity-50"
                        >
                            <X size={20} className="text-white" strokeWidth={3} />
                        </button>

                        <h2 className="text-2xl font-black uppercase mb-4 text-center">
                            {t?.previewEmailTitle || "Kart Önizleme ve Onay"}
                        </h2>
                        
                        {previewApproval.authorEmail ? (
                            <div className="mb-4 w-full px-4">
                                <label className="flex items-center gap-3 font-bold cursor-pointer bg-gray-100 p-3 border-2 border-black w-full text-sm">
                                    <input 
                                        type="checkbox"
                                        checked={sendApproveEmail}
                                        onChange={(e) => setSendApproveEmail(e.target.checked)}
                                        className="w-5 h-5 border-2 border-black accent-[#7ED957] flex-shrink-0"
                                    />
                                    <span>Kullanıcıya onay e-postası eklentiyle gönderilsin mi? <br/><span className="text-gray-500 font-normal">({previewApproval.authorEmail})</span></span>
                                </label>
                            </div>
                        ) : (
                            <p className="mb-4 text-gray-500 italic text-center text-sm w-full">Kullanıcının e-posta adresi olmadığı için bildirim gönderilemez.</p>
                        )}
                        
                        <p className="text-center font-bold text-gray-700 mb-4 px-4 text-sm">
                            {(sendApproveEmail && previewApproval.authorEmail) ? 
                                `Bu kelime onaylandığında "${previewApproval.authorEmail}" adresine aşağıdaki görsel ile bir teşekkür e-postası GÖNDERİLECEKTİR:` 
                                : `Bu kelime e-posta GÖNDERİLMEDEN sadece sözlüğe eklenecektir. Aşağıdaki kart önizlemedir:`}
                        </p>

                        <div className="w-full max-w-[300px] sm:max-w-[400px] mb-8 bg-gray-100 p-4 border-2 border-dashed border-gray-400">
                            <WordCard 
                                ref={previewCardRef}
                                data={previewApproval} 
                                lang={lang} 
                            />
                        </div>

                        <div className="flex gap-4 w-full">
                            <button
                                onClick={() => setPreviewApproval(null)}
                                disabled={isApproving}
                                className="flex-1 py-3 font-black uppercase border-2 border-black bg-gray-200 hover:bg-gray-300 transition-colors disabled:opacity-50"
                            >
                                {t?.cancelBtn || 'İptal'}
                            </button>
                            <button
                                onClick={confirmApprove}
                                disabled={isApproving}
                                className="flex-1 py-3 font-black uppercase border-2 border-black bg-[#7ED957] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-1 transition-all disabled:opacity-50 flex justify-center items-center gap-2"
                            >
                                {isApproving ? (t?.processing || 'İşleniyor...') : (t?.confirmApproveBtn || 'Onayla ve Gönder')}
                                {!isApproving && <Check size={20} />}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Manage;
